import { useMemo } from 'react';
import { hasEntry } from '../../../utils/utils';
import type { BalanceSheetResponse, ClientBalanceAccountDto } from '../../generated/client';
import { useBalanceSheet } from './use-balance-sheet';

interface IWealthData {
    assets?: ClientBalanceAccountDto;
    assetsTotal: number;
    liabilities?: ClientBalanceAccountDto;
    liabilitiesTotal: number;
    income?: ClientBalanceAccountDto;
    incomeTotal: number;
    expenses?: ClientBalanceAccountDto;
    expensesTotal: number;
    jbAssets?: number;
    balanceSheetResponse?: BalanceSheetResponse;
    isLoading?: boolean;
}

const emptyWealthResult: IWealthData = {
    assets: undefined,
    assetsTotal: 0,
    liabilities: undefined,
    liabilitiesTotal: 0,
    income: undefined,
    incomeTotal: 0,
    expenses: undefined,
    expensesTotal: 0,
    jbAssets: 0,
    isLoading: true,
};

export function useWealth(): IWealthData {
    const { data: balanceSheetResponse, isSuccess, isLoading } = useBalanceSheet();

    return useMemo(() => {
        if (!isSuccess || !hasEntry(balanceSheetResponse?.data.balanceEntries)) {
            return emptyWealthResult;
        }
        const assets = balanceSheetResponse?.data.balanceEntries.find((wealthSection) => wealthSection.accountType === 'ASSET');
        const assetsTotal = assets?.totalAmount ?? 0;

        const liabilities = balanceSheetResponse?.data.balanceEntries.find((wealthSection) => wealthSection.accountType === 'LIABILITY');
        const liabilitiesTotal = liabilities?.totalAmount ?? 0;

        const income = balanceSheetResponse?.data.balanceEntries.find((wealthSection) => wealthSection.accountType === 'INCOME');
        const incomeTotal = income?.totalAmountCurrentYear ?? 0;

        const expenses = balanceSheetResponse?.data.balanceEntries.find((wealthSection) => wealthSection.accountType === 'EXPENSE');
        const expensesTotal = expenses?.totalAmountCurrentYear ?? 0;

        const jbAssets = balanceSheetResponse?.data.jbAssets;

        return { assets, assetsTotal, liabilities, liabilitiesTotal, income, incomeTotal, expenses, expensesTotal, jbAssets, balanceSheetResponse, isLoading };
    }, [balanceSheetResponse, isSuccess, isLoading]);
}
